import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import { linksService } from "../../services/api";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import toast from "react-hot-toast";

function LinksPage() {
  const [links, setLinks] = useState([]);
  const [pagination, setPagination] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    sortBy: "created_at",
    sortOrder: "desc",
    page: 1,
    limit: 20,
  });

  useEffect(() => {
    loadLinks();
  }, [filters]);

  const loadLinks = async () => {
    try {
      setIsLoading(true);
      const response = await linksService.getAll(filters);

      if (response.data.success) {
        setLinks(response.data.data.links);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      toast.error("Linkler yüklenirken bir hata oluştu");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, page: 1 });
  };

  const handleSort = (sortBy) => {
    const sortOrder =
      filters.sortBy === sortBy && filters.sortOrder === "desc"
        ? "asc"
        : "desc";
    setFilters({ ...filters, sortBy, sortOrder, page: 1 });
  };

  const handlePageChange = (page) => {
    setFilters({ ...filters, page });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link kopyalandı!");
  };

  const handleDeleteLink = async (id) => {
    if (!window.confirm("Bu linki silmek istediğinizden emin misiniz?")) {
      return;
    }

    try {
      await linksService.delete(id);
      toast.success("Link başarıyla silindi");
      loadLinks();
    } catch (error) {
      toast.error("Link silinirken bir hata oluştu");
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await linksService.update(id, { isActive: !currentStatus });
      toast.success(
        currentStatus ? "Link devre dışı bırakıldı" : "Link aktif edildi"
      );
      loadLinks();
    } catch (error) {
      toast.error("Link durumu güncellenirken bir hata oluştu");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Linklerim</h1>
          <p className="text-gray-600">Kısaltılmış linklerinizi yönetin</p>
        </div>
        <Link
          to="/dashboard/links/create"
          className="btn btn-primary mt-4 sm:mt-0"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Yeni Link Oluştur
        </Link>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <form
            onSubmit={handleSearch}
            className="flex flex-col sm:flex-row gap-4"
          >
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Link, başlık veya URL ara..."
                  className="form-input pl-10"
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                />
              </div>
            </div>
            <select
              className="form-select"
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split("-");
                setFilters({ ...filters, sortBy, sortOrder, page: 1 });
              }}
            >
              <option value="created_at-desc">En Yeni</option>
              <option value="created_at-asc">En Eski</option>
              <option value="click_count-desc">En Çok Tıklanan</option>
              <option value="total_earnings-desc">En Çok Kazandıran</option>
              <option value="title-asc">Başlık A-Z</option>
            </select>
          </form>
        </div>
      </div>

      {/* Links Grid */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" text="Linkler yükleniyor..." />
        </div>
      ) : links.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {links.map((link) => (
            <div
              key={link.id}
              className="card hover:shadow-md transition-shadow"
            >
              <div className="card-body">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate mb-1">
                      {link.title || "Başlıksız Link"}
                    </h3>
                    <p className="text-sm text-gray-500 truncate mb-2">
                      {link.originalUrl}
                    </p>
                    <div className="flex items-center space-x-2">
                      <code className="text-sm text-primary-600 bg-primary-50 px-2 py-1 rounded">
                        {process.env.REACT_APP_BASE_URL}/{link.shortCode}
                      </code>
                      <button
                        onClick={() => copyToClipboard(link.shortUrl)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Kopyala"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 ml-4">
                    <span
                      className={`badge ${link.isActive ? "badge-success" : "badge-gray"}`}
                    >
                      {link.isActive ? "Aktif" : "Pasif"}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-xl font-bold text-gray-900">
                      {link.clickCount}
                    </p>
                    <p className="text-xs text-gray-600">Tıklama</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-green-600">
                      ${link.totalEarnings?.toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-600">Kazanç</p>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">
                    {new Date(link.createdAt).toLocaleDateString("tr-TR")}
                  </span>
                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/dashboard/links/${link.id}/stats`}
                      className="btn btn-secondary btn-sm"
                      title="İstatistikler"
                    >
                      <EyeIcon className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => handleToggleActive(link.id, link.isActive)}
                      className={`btn btn-sm ${link.isActive ? "btn-warning" : "btn-success"}`}
                      title={link.isActive ? "Devre Dışı Bırak" : "Aktif Et"}
                    >
                      {link.isActive ? "Durdur" : "Başlat"}
                    </button>
                    <button
                      onClick={() => handleDeleteLink(link.id)}
                      className="btn btn-danger btn-sm"
                      title="Sil"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {link.expiresAt && (
                  <div className="mt-2 p-2 bg-yellow-50 rounded text-xs text-yellow-800">
                    Bitiş:{" "}
                    {new Date(link.expiresAt).toLocaleDateString("tr-TR")}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="card-body text-center py-12">
            <LinkIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Henüz link oluşturmamışsınız
            </h3>
            <p className="text-gray-600 mb-6">
              İlk linkinizi oluşturun ve para kazanmaya başlayın
            </p>
            <Link to="/dashboard/links/create" className="btn btn-primary">
              <PlusIcon className="w-4 h-4 mr-2" />
              İlk Linkinizi Oluşturun
            </Link>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-700">
                Sayfa {pagination.currentPage} / {pagination.totalPages}(
                {pagination.totalItems} toplam link)
              </p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrev}
                  className="btn btn-secondary btn-sm"
                >
                  Önceki
                </button>
                <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded text-sm">
                  {pagination.currentPage}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNext}
                  className="btn btn-secondary btn-sm"
                >
                  Sonraki
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LinksPage;
